import { RootState } from '../store';
import { Dict } from 'mixpanel-browser';
import { ANALYTICS } from '@/constants/analytics/analytics';
import { SESSION_KEYS } from '@/constants/storage/storage';
import { cookiesUtils } from '@/utils';
import { COOKIE_KEYS } from '@/constants/storage/cookies';
import moment from 'moment';
import { EGroupItemType, EGroupType, Group, GroupItem } from '../../models';
import { isUserAnonymous } from '../../utils/helpers/user';
import { createSelector } from '@reduxjs/toolkit';
import {
  isClickStreamOptedIn,
  isExtendedDataOptedIn,
} from '../../utils/helpers/opt-in';

// Base selectors
const selectUser = (state: RootState) => state.user;
const selectExtensionInfo = (state: RootState) => state.extensionInfo;
const selectFlagsConfig = (state: RootState) => state.flagsConfig;
const selectPasswordProtection = (state: RootState) => state.passwordProtection;
const selectGroups = (state: RootState) => state.groups;

export const selectAnalyticsSuperAttributes = createSelector(
  [
    selectUser,
    selectExtensionInfo,
    selectFlagsConfig,
    selectPasswordProtection,
    selectGroups,
  ],
  (user, extensionInfo, flagsConfig, passwordProtection, groups): Dict => {
    /**
     * get user premium tier
     */
    const getTier = (): string => {
      let tier = ANALYTICS.VALUES.ANONYMOUS;
      if (!isUserAnonymous(user)) {
        tier = ANALYTICS.VALUES.FREE;
      }
      if (user.isPremium) {
        tier = ANALYTICS.VALUES.PREMIUM;
      }
      return tier;
    };

    /**
     * get user permission: opt in | web history
     */
    const getPermissions = (): string[] => {
      const permissions = [];
      const webHistoryToken = !!cookiesUtils.getCookie(
        COOKIE_KEYS.GOOGLE_AUTH_UUID
      );
      if (webHistoryToken) {
        permissions.push(ANALYTICS.PERMISSION.OAUTH);
      }
      if (isClickStreamOptedIn(flagsConfig)) {
        permissions.push(ANALYTICS.PERMISSION.OPT_IN);
      }
      if (isExtendedDataOptedIn(flagsConfig)) {
        permissions.push(ANALYTICS.PERMISSION.OPT_IN_BUCKETS);
      }
      if (flagsConfig.eulaAgreed) {
        permissions.push(ANALYTICS.PERMISSION.TERMS_AND_CONDITIONS);
      }
      if (flagsConfig.consentAffiliates) {
        permissions.push(ANALYTICS.PERMISSION.AFFILIATE);
      }
      if (flagsConfig.consentThirdParties) {
        permissions.push(ANALYTICS.PERMISSION.THIRD_PARTY);
      }
      return permissions;
    };

    /**
     * get is password protection enabled
     */
    const isPasswordProtectionEnabled = (): boolean => {
      return (
        passwordProtection.blockSettings ||
        passwordProtection.blockBlockedPage ||
        passwordProtection.addChallenge ||
        passwordProtection.notifyUninstall
      );
    };

    /**
     * get has install prevention
     */
    const hasUninstallPrevention = (): boolean => {
      return passwordProtection.notifyUninstall;
    };

    /**
     * get premium details if user is premium
     */
    const getSubscriptionAttributes = (): Record<string, unknown> => {
      const attributes: Record<string, unknown> = {
        [ANALYTICS.PROPERTIES.SUBSCRIPTION_STATUS]: '',
        [ANALYTICS.PROPERTIES.SUBSCRIPTION_IS_ON_TRIAL]: '',
        [ANALYTICS.PROPERTIES.SUBSCRIPTION_PLAN]: '',
        [ANALYTICS.PROPERTIES.SUBSCRIPTION_AGE]: '',
      };
      if (!user.isPremium) {
        return {};
      }
      const details = user.premiumDetails;
      if (!details) return {};
      const now = moment();
      const subscriptionCreated = moment(details.createdAt);
      const daysDiff = now.diff(subscriptionCreated, 'days');
      attributes[ANALYTICS.PROPERTIES.SUBSCRIPTION_STATUS] = details.isCancelled
        ? 'Inactive'
        : 'Active';
      attributes[ANALYTICS.PROPERTIES.SUBSCRIPTION_IS_ON_TRIAL] =
        details.isTrialActive;
      attributes[ANALYTICS.PROPERTIES.SUBSCRIPTION_PLAN] = details.duration;
      attributes[ANALYTICS.PROPERTIES.SUBSCRIPTION_AGE] = daysDiff;
      return attributes;
    };

    /**
     * get user age attribute
     */
    const getUserAgeAttribute = (): Dict => {
      const installedTime = extensionInfo.installedTime;
      if (!installedTime)
        return {
          [ANALYTICS.PROPERTIES.INSTALL_AGE]: null,
        };
      const now = moment();
      const installed = moment(installedTime);
      const daysDiff = now.diff(installed, 'days');
      return { [ANALYTICS.PROPERTIES.INSTALL_AGE]: daysDiff };
    };

    /**
     * get blocking super attributes
     */
    const getBlockingSuperAttributes = (): Record<string, any> => {
      const numberOfBlockedItems = 'Number of Blocked Items';
      const numberOfBlockedWebsites = 'Number of Blocked Websites';
      const numberOfBlockedKeywords = 'Number of Blocked Keywords';
      const numberOfBlockedCategories = 'Number of Blocked Categories';
      const numberOfBlockedApps = 'Number of Blocked Apps';
      const websitesBlockList = 'Websites Block List';
      const categoriesBlockList = 'Categories Block List';
      const keywordsBlockList = 'Keywords Block List';
      const appsBlockList = 'Apps Block List';
      const blockModeUsage = 'Block Mode Usage';

      const res: Record<string, any> = {
        [numberOfBlockedItems]: 0,
        [numberOfBlockedWebsites]: 0,
        [numberOfBlockedKeywords]: 0,
        [numberOfBlockedCategories]: 0,
        [numberOfBlockedApps]: 0,
        [websitesBlockList]: new Set<string>(),
        [categoriesBlockList]: new Set<string>(),
        [keywordsBlockList]: new Set<string>(),
        [appsBlockList]: new Set<string>(),
        [blockModeUsage]: new Set<string>(),
      };
      groups.forEach((group: Group) => {
        switch (group.type) {
          case EGroupType.schedule:
            res[blockModeUsage].add('Schedule');
            break;
          case EGroupType.focusTime:
            res[blockModeUsage].add('Focus Mode');
            break;
          case EGroupType.usageLimit:
            res[blockModeUsage].add('Time Limit');
            break;
        }
        group.items.forEach((item: GroupItem) => {
          res[numberOfBlockedItems]++;
          switch (item.itemType) {
            case EGroupItemType.website:
              res[numberOfBlockedWebsites]++;
              res[websitesBlockList].add(item.itemValue);
              break;
            case EGroupItemType.category:
              res[numberOfBlockedCategories]++;
              res[categoriesBlockList].add(item.itemValue);
              break;
            case EGroupItemType.keyword:
              res[numberOfBlockedKeywords]++;
              res[keywordsBlockList].add(item.itemValue);
              break;
            case EGroupItemType.appIos:
            case EGroupItemType.appAndroid:
              res[numberOfBlockedApps]++;
              res[appsBlockList].add(item.itemValue);
              break;
          }
        });
      });

      res[blockModeUsage] = Array.from(res[blockModeUsage]);
      res[websitesBlockList] = Array.from(res[websitesBlockList]);
      res[categoriesBlockList] = Array.from(res[categoriesBlockList]);
      res[keywordsBlockList] = Array.from(res[keywordsBlockList]);
      res[appsBlockList] = Array.from(res[appsBlockList]);
      return res;
    };

    return {
      [ANALYTICS.PROPERTIES.SITE_VERSION]: 'v2',
      [ANALYTICS.PROPERTIES.IS_SP_INFRA]: true,
      [ANALYTICS.PROPERTIES.PLATFORM]: ANALYTICS.VALUES.WEB,
      [ANALYTICS.PROPERTIES.URL_PATH]: window.location.pathname.substring(1),
      [ANALYTICS.PROPERTIES.IS_EXTENSION]: true,
      [ANALYTICS.PROPERTIES.TIER]: getTier(),
      [ANALYTICS.PROPERTIES.PERMISSIONS]: getPermissions(),
      [ANALYTICS.PROPERTIES.IS_TEST_USER]:
        user?.email?.includes('@test.com') ||
        user?.email?.includes('@blocksite.co'),
      [ANALYTICS.PROPERTIES.FIREBASE_ID]: user?.uid,
      [ANALYTICS.PROPERTIES.UUID]: user?.uid,
      [ANALYTICS.PROPERTIES.HAS_PASSWORD_PROTECTION]:
        isPasswordProtectionEnabled(),
      [ANALYTICS.PROPERTIES.HAS_PASSWORD_PREVENTION]: hasUninstallPrevention(),
      [ANALYTICS.PROPERTIES.PAGE_TYPE]:
        sessionStorage.getItem(SESSION_KEYS.PAGE_VIEW_TYPE) || 'Other',
      // Installed extension version; only registered when known (see useMixpanel.updateSuperAttributes).
      ...(extensionInfo.extensionVersion
        ? { [ANALYTICS.PROPERTIES.APP_VERSION]: extensionInfo.extensionVersion }
        : {}),
      ...getSubscriptionAttributes(),
      ...getUserAgeAttribute(),
      ...getBlockingSuperAttributes(),
    };
  }
);
